import { useMutation, useQueryClient } from '@tanstack/react-query';
import { transcriptsService } from '../services/transcripts.service';
import type {
  Segment,
  SaveTranscriptPayload,
  AutoTranscribePayload,
} from '../types/api.types';
import toast from 'react-hot-toast';
import { segmentKeys } from './useSegments';

// Save/update manual transcription
export function useSaveTranscript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveTranscriptPayload) =>
      transcriptsService.saveTranscript(payload),
    onSuccess: (_data, variables) => {
      // Immediately update the cache with the saved transcript text so that
      // navigating away and back shows the correct data without waiting for the
      // background refetch to complete (eliminates the stale-data race window).
      queryClient.setQueriesData<Segment[]>(
        { queryKey: segmentKeys.all, exact: false },
        (oldSegments) => {
          if (!oldSegments) return oldSegments;
          const now = new Date().toISOString();
          return oldSegments.map((seg) => {
            if (seg.id !== variables.segmentId) return seg;
            const manualTranscripts = seg.transcripts.filter((t) => t.source === 'manual');
            if (manualTranscripts.length > 0) {
              // Update the most-recent manual transcript in-place
              const latestId = Math.max(...manualTranscripts.map((t) => t.id));
              return {
                ...seg,
                transcripts: seg.transcripts.map((t) =>
                  t.id === latestId
                    ? { ...t, transcription: variables.transcription, updatedAt: now }
                    : t,
                ),
              };
            }
            // No existing manual transcript — add a placeholder; the background
            // refetch will replace it with the real server record.
            return {
              ...seg,
              transcripts: [
                ...seg.transcripts,
                {
                  id: Date.now(), // high temp id; real id arrives on refetch
                  segmentId: variables.segmentId,
                  name: variables.name,
                  transcription: variables.transcription,
                  source: 'manual' as const,
                  lastAccessed: now,
                  createdAt: now,
                  updatedAt: now,
                },
              ],
            };
          });
        },
      );
      // Invalidate in background to sync the real server data (e.g. authoritative id)
      queryClient.invalidateQueries({
        queryKey: segmentKeys.all,
      });
      toast.success('Transcription saved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save transcription: ${error.message}`);
    },
  });
}

// Request auto-transcription
export function useAutoTranscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AutoTranscribePayload) =>
      transcriptsService.autoTranscribe(payload),
    onSuccess: () => {
      // Invalidate segments to refresh transcripts
      queryClient.invalidateQueries({
        queryKey: segmentKeys.all,
      });
      toast.success('Auto-transcription complete');
    },
    onError: (error: Error) => {
      toast.error(`Failed to auto-transcribe: ${error.message}`);
    },
  });
}
